import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Container from '@common/components/container';
import Overview from '@common/components/overview';
import { TopNavigationBar } from '@common/components/navigation-bar';
import { Button } from '@common/components/button';
import DataTable from '@common/components/datatable';
import useColumn from './useColumn';
import { Bar } from '@common/components/bar';
import CreateEvaluationModal from '@modules/performance/components/create-evaluation-modal/create-evaluation-modal';
import { useHistory, useParams } from 'react-router';
import Api, { useRequest } from '@common/services/api';
import { useAppSelector, useDidComponentMount } from '@common/hooks';
import { selected } from '@modules/organisation/selectors/organisation';
import {
  EPerformanceFormStatus,
  EvaluationFormTypes,
  EPredicateFields,
  EPredicateOperators,
} from '@common/definitions';
import { EPredicateFilters } from '@common/components/predicates-filter';
import { useEncodedFullPath } from '@common/hooks/url';
import { FormsData, FormsMeta } from '@modules/performance/types';
import { AlertService } from '@common/services/alert';
import FiltersToolbar from '@common/components/filters-toolbar/filters-toolbar';
import { omit } from 'lodash';
import Placeholder from '@common/components/placeholder';
import ModalWindow from '@common/components/modal/modal-window';
import Video from '@common/components/video';

const EvaluationForms = () => {
  const { t } = useTranslation();
  const organisation = useAppSelector(selected);
  const didMount = useDidComponentMount();
  const params = useParams<{ networkId: string }>();
  const basePath = params.networkId
    ? `/networks/${params.networkId}`
    : '/admin';
  const encodedPath = useEncodedFullPath();
  const [search, setSearch] = useState<string>('');
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const users = useAppSelector((state) => Object.values(state.users.items));
  const defaultLanguage = useAppSelector(
    (state) => state.loggedUser.user.language_locale
  );

  const typeFilterOptions = [
    { label: t('performance:manager'), value: EvaluationFormTypes.MANAGER },
    {
      label: t('performance:manager_employee'),
      value: EvaluationFormTypes.MANAGER_EMPLOYEE,
    },
  ];

  const statusFilterOptions = [
    { label: t('performance:draft'), value: EPerformanceFormStatus.DRAFT },
    { label: t('performance:live'), value: EPerformanceFormStatus.LIVE },
    {
      label: t('performance:archived'),
      value: EPerformanceFormStatus.ARCHIVED,
    },
    { label: t('performance:deleted'), value: EPerformanceFormStatus.DELETED },
  ];

  const [predicates, setPredicates] = useState([
    {
      filter: EPredicateFilters[EPredicateFields.PERFORMANCE_FORM_TYPE],
      value: {
        attribute: EPredicateFields.PERFORMANCE_FORM_TYPE,
        comparison: EPredicateOperators.EQ,
        value: '',
      },
      options: typeFilterOptions,
    },
    {
      filter: EPredicateFilters[EPredicateFields.PERFORMANCE_FORM_STATUS],
      value: {
        attribute: EPredicateFields.PERFORMANCE_FORM_STATUS,
        comparison: EPredicateOperators.EQ,
        value: '',
      },
      options: statusFilterOptions,
    },
    {
      filter: EPredicateFilters[EPredicateFields.PERFORMANCE_FORM_CREATED_BY],
      value: {
        attribute: EPredicateFields.PERFORMANCE_FORM_CREATED_BY,
        comparison: EPredicateOperators.EQ,
        value: '',
      },
      options: users || [],
    },
  ]);

  const history = useHistory();

  const handleOpenModule = useCallback(
    (type: EvaluationFormTypes) => {
      history.push(
        `${basePath}/performance/forms/create?back=${encodedPath}&type=${type}`
      );
    },
    [history, basePath, encodedPath]
  );

  const [formTypeId, formStatusId, userId] = useMemo(() => {
    return [
      predicates.find(
        (p) => p.filter === EPredicateFilters[EPredicateFields.PERFORMANCE_FORM_TYPE]
      )?.value.value,
      predicates.find(
        (p) => p.filter ===
          EPredicateFilters[EPredicateFields.PERFORMANCE_FORM_STATUS]
      )?.value.value,
      predicates.find(
        (p) => p.filter ===
          EPredicateFilters[EPredicateFields.PERFORMANCE_FORM_CREATED_BY]
      )?.value.value,
    ];
  }, [predicates]);

  const payload = useMemo(
    () => ({
      ...(formTypeId && formTypeId !== '' ? { type: formTypeId } : {}),
      ...(formStatusId && formStatusId !== '' ? { status: formStatusId } : {}),
      ...(userId && userId !== '' ? { createdBy: userId } : {}),
      ...(search && search !== '' ? { q: search } : {}),
    }),
    [formTypeId, formStatusId, search, userId]
  );

  const apiConfig = {
    useCursor: true,
    limit: 20,
    payload,
    transform: (res: any) => {
      return {
        ...res,
        data: res.data.map((form: FormsData) => ({
          ...form,
          created_by: res.meta.related.users.find(
            (user: any) => user.id === form.created_by
          ),
        })),
      };
    },
  };

  const {
    data: evaluationFormsData,
    meta: metaData,
    setData: setForms,
    loading,
    refetch,
    reset,
  } = useRequest(
    `/v1/organisations/${organisation.id}/performance/forms`,
    apiConfig
  );

  const handleDeleteForm = useCallback(
    async (form: FormsData) => {
      try {
        await Api.delete(
          `/v1/organisations/${organisation.id}/performance/forms/${form.id}`
        );
        setForms((prevForms: FormsData[]) => prevForms.filter((f) => f.id !== form.id)
        );
        return AlertService.success(t('performance:form_deleted'));
      } catch (error: any) {
        AlertService.forStatus(error.status, {
          warning: t('performance:form_delete_warning'),
          error: t('performance:form_delete_error'),
        });
      }
    },
    [organisation.id, setForms]
  );

  const handleArchiveForm = useCallback(
    async (form: FormsData) => {
      try {
        await Api.post(
          `/v1/organisations/${organisation.id}/performance/forms/${form.id}/archive`
        );
        setForms((prevForms: FormsData[]) => prevForms.filter((f) => f.id !== form.id)
        );
        AlertService.success(t('performance:form_archived'));
      } catch (error: any) {
        AlertService.forStatus(error.status, {
          warning: t('performance:form_archive_warning'),
          error: t('performance:form_archive_error'),
        });
      }
    },
    [organisation.id, setForms]
  );

  const handleUnarchiveForm = useCallback(
    async (form: FormsData) => {
      try {
        await Api.post(
          `/v1/organisations/${organisation.id}/performance/forms/${form.id}/unarchive`
        );
        setForms((prevForms: FormsData[]) => prevForms.filter((f) => f.id !== form.id)
        );
        AlertService.success(t('performance:form_unarchived'));
      } catch (error: any) {
        AlertService.forStatus(error.status, {
          warning: t('performance:form_unarchive_warning'),
          error: t('performance:form_unarchive_error'),
        });
      }
    },
    [organisation.id, setForms]
  );

  const columnOptions = {
    handleDeleteForm,
    handleArchiveForm,
    handleUnarchiveForm,
  };

  const handleCloseVideoModal = () => {
    setIsVideoModalOpen(false);
  };

  const handleOpenVideoModal = () => {
    setIsVideoModalOpen(true);
  };

  useEffect(() => {
    if (!didMount) return;
    reset();
  }, [payload, didMount, reset]);

  const meta: FormsMeta = metaData;
  const columns = useColumn(evaluationFormsData, columnOptions);

  return (
    <>
      <Container name="evaluation-forms">
        <TopNavigationBar
          title={t('performance:evaluation_forms')}
          {...(defaultLanguage === 'nl' && {
            learnMoreText: (
              <span
                className="tw-text-gray-500 tw-text-base tw-ml-6 tw-font-normal
            tw-leading-5 hover:tw-text-gray-900 hover:tw-underline hover:tw-cursor-pointer"
                onClick={handleOpenVideoModal}
              >
                {t('core:learn_more')}
              </span>
            ),
          })}
          action={
            <CreateEvaluationModal onOpenCourse={handleOpenModule}>
              <Button icon="add" type="primary" size="large">
                {t('performance:new_form')}
              </Button>
            </CreateEvaluationModal>
          }
        />
        <Container.Content>
          <Bar>
            <FiltersToolbar
              predicates={predicates}
              setPredicates={setPredicates}
              organisation={organisation}
              onSearch={setSearch}
              searchPlaceholder={t('common:search_list_search_placeholder')}
            />
          </Bar>
          <Overview>
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-200">
              <DataTable
                columns={columns}
                data={evaluationFormsData}
                loading={loading}
                onShowMore={refetch}
                pagination={{
                  ...omit(apiConfig, 'payload'),
                  ...meta?.pagination,
                }}
                emptyView={
                  <Placeholder
                    title={t('performance:no_evaluation_forms')}
                    icon="grid_view"
                  />
                }
              />
            </div>
          </Overview>
        </Container.Content>
      </Container>
      <ModalWindow
        show={isVideoModalOpen}
        onHide={handleCloseVideoModal}
        size="large"
        className="modal--video !tw-overflow-hidden"
      >
        <div className="!tw-overflow-hidden">
          <Video
            path="https://www.youtube.com/watch?v=-DYGyJxZPi8"
            className="tw-w-full tw-h-full"
            playing
          />
        </div>
      </ModalWindow>
    </>
  );
};

export default EvaluationForms;
